import { Box, Form, Popover, Toolbar } from '@bynder/design-system';
import { IconLink } from '@bynder/icons';
import type { Editor } from '@tiptap/react';
import { useState } from 'react';
import {
  getLinkFromSelection,
  getTextFromSelection,
} from '../../helpers/tiptap.ts';

import { EditLink } from './edit-link.tsx';
import { PreviewLink } from './preview-link.tsx';

export const formatTextAsLink = ({
  editor,
  href,
  target,
  text = href,
}: LinkEditorState & {
  editor: Editor;
}) => {
  editor
    .chain()
    .focus()
    // mark selection as a link, if there is no selection, it creates one at the cursor
    .extendMarkRange('link')
    .setLink({ href, target })
    .command((editor) => {
      const transaction = editor.tr;
      transaction.insertText(text);
      return true;
    })
    .run();
};

export type LinkEditorState = {
  href: string;
  text: string;
  target: '_blank' | null;
};

export const LinkEditor = ({ editor }: { editor: Editor }) => {
  let togglePopover: () => void;
  const [state, setState] = useState<LinkEditorState>({
    href: '',
    text: '',
    target: null,
  });
  const [view, setView] = useState<'preview' | 'edit'>('edit');

  const setDefaultTextOnDropdownOpen = (editor: Editor) => {
    // if the user has selected a link, populate the fields with the link's data
    const existingLink = getLinkFromSelection(editor);
    if (existingLink) {
      setState({
        href: existingLink.attrs.href,
        target: existingLink.attrs.target,
        text: existingLink.text,
      });
      setView('preview');
      return;
    }

    // if the user has selected text, populate the text field with the selected text
    const textFromSelection = getTextFromSelection(editor);
    const textIsEmpty = state.text === '';
    if (textIsEmpty && textFromSelection !== null) {
      setState({ href: '', target: null, text: textFromSelection });
      setView('edit');
      return;
    }

    // reset the fields and treat as a new link
    setState({ href: '', text: '', target: null });
    setView('edit');
  };

  return (
    <Popover
      trigger={({ isOpen, onClick, ...triggerProps }) => {
        togglePopover = onClick;
        return (
          <Toolbar.Button
            isPressed={isOpen || editor.isActive('link')}
            isFullWidth
            {...{ ...triggerProps, onClick }}
            aria-haspopup="listbox"
            aria-label="open Hyperlink editor"
          >
            <IconLink />
          </Toolbar.Button>
        );
      }}
      onOpen={() => {
        setDefaultTextOnDropdownOpen(editor);
      }}
      aria-label="Hyperlink editor"
      noPadding
    >
      <Box padding="4">
        <Form
          // @ts-expect-error style is not defined as a `Form` property, but it still spread into the raw HTMLForm element
          style={{ width: 415 }}
          onSubmit={(evt) => {
            evt.preventDefault();
            if (state.href) {
              // if the user didn't specify any text, use the href instead
              formatTextAsLink({
                editor,
                ...state,
                text: state.text || state.href,
              });
              togglePopover();
            }
          }}
        >
          {view === 'preview' ? (
            <PreviewLink
              href={state.href}
              onEdit={() => setView('edit')}
              onUnlink={() => {
                editor.commands.unsetLink();
                setState({ href: '', text: '', target: null });
                togglePopover();
              }}
            />
          ) : (
            <EditLink linkEditorState={state} setLinkEditorState={setState} />
          )}
        </Form>
      </Box>
    </Popover>
  );
};
